import type * as simplewebauthn from '@simplewebauthn/server';

import type { Binary } from '@code-expert/mongo-ts';
import {
  array,
  base64,
  eq,
  iots,
  nonEmptyArray,
  option,
  pipe,
  string,
  tagged,
} from '@code-expert/prelude';
import type { UserId, WebAuthId } from '/imports/domain/identity';
import type { ConnectionId } from './connection';

export const unknownUser = 'unknown' as const;

export const authenticatorTransportFutureKeys = {
  ble: null,
  cable: null,
  hybrid: null,
  internal: null,
  nfc: null,
  'smart-card': null,
  usb: null,
} satisfies Record<simplewebauthn.AuthenticatorTransportFuture, null>;

export const AuthenticatorTransportFuture = iots.keyof(authenticatorTransportFutureKeys);

export interface Authenticator {
  id: string;
  name: string;
  connectionId?: ConnectionId;
  registeredAt: Date;
  credentialID: Binary;
  credentialPublicKey: Binary;
  counter: number;
  transports?: Array<simplewebauthn.AuthenticatorTransportFuture>;
}

export interface WebAuth {
  _id: WebAuthId;
  userId: UserId | typeof unknownUser;
  registrationChallenge?: string;
  loginChallenge?: string;
  authenticators: Array<Authenticator>;
}

export type PublicAuthenticator = Pick<
  Authenticator,
  'id' | 'registeredAt' | 'connectionId' | 'name'
>;

export const publicAuthenticatorEq: eq.Eq<PublicAuthenticator> = pipe(
  eq.nullable(string.Eq),
  eq.contramap((x: PublicAuthenticator) => x.connectionId),
);

export type WebAuthStatus =
  | tagged.Tagged<'notRegistered'>
  | tagged.Tagged<'registered', { authenticators: NonEmptyArray<PublicAuthenticator> }>
  | tagged.Tagged<
      'auth',
      {
        activeAuthenticator: PublicAuthenticator;
        authenticators: NonEmptyArray<PublicAuthenticator>;
      }
    >;

export const webAuthStatusAdt = tagged.build<WebAuthStatus>();

export interface WebAuthPublic {
  _id: WebAuthId;
  status: WebAuthStatus;
}

export const webAuthToWebAuthPublic =
  (connectionId: ConnectionId) =>
  (webAuth: WebAuth): WebAuthPublic => ({
    _id: webAuth._id,
    status: pipe(
      nonEmptyArray.fromArray(webAuth.authenticators),
      option.match(webAuthStatusAdt.wide.notRegistered, (authenticators) =>
        pipe(
          authenticators,
          array.findFirst(
            (a) => a.connectionId != null && string.Eq.equals(a.connectionId, connectionId),
          ),
          option.fold(
            () => webAuthStatusAdt.wide.registered({ authenticators }),
            (activeAuthenticator) =>
              webAuthStatusAdt.wide.auth({ activeAuthenticator, authenticators }),
          ),
        ),
      ),
    ),
  });

export const PublicKeyCredentialType = iots.literal('public-key');

const COSEAlgorithmIdentifier = iots.number;

export const AuthenticatorAttestationResponseJSON = iots.struct(
  {
    clientDataJSON: base64.Base64Url,
    attestationObject: base64.Base64Url,
  },
  {
    authenticatorData: base64.Base64Url,
    transports: iots.array(AuthenticatorTransportFuture),
    publicKeyAlgorithm: COSEAlgorithmIdentifier,
    publicKey: base64.Base64Url,
  },
);

export const CredentialPropertiesOutput = iots.partial({
  rk: iots.boolean,
});

export const AuthenticationExtensionsClientOutputs = iots.partial({
  appid: iots.boolean,
  credProps: CredentialPropertiesOutput,
  hmacCreateSecret: iots.boolean,
});

const AuthenticatorAttachment = iots.keyof({ 'cross-platform': null, platform: null });

export const RegistrationResponseJSON = iots.struct(
  {
    id: base64.Base64Url,
    rawId: base64.Base64Url,
    response: AuthenticatorAttestationResponseJSON,
    clientExtensionResults: AuthenticationExtensionsClientOutputs,
    type: PublicKeyCredentialType,
  },
  {
    authenticatorAttachment: AuthenticatorAttachment,
  },
);

export const AuthenticatorAssertionResponseJSON = iots.struct(
  {
    clientDataJSON: base64.Base64Url,
    authenticatorData: base64.Base64Url,
    signature: base64.Base64Url,
  },
  {
    userHandle: base64.Base64Url,
  },
);

export const AuthenticationResponseJSON = iots.struct(
  {
    id: base64.Base64Url,
    rawId: base64.Base64Url,
    response: AuthenticatorAssertionResponseJSON,
    clientExtensionResults: AuthenticationExtensionsClientOutputs,
    type: PublicKeyCredentialType,
  },
  {
    authenticatorAttachment: AuthenticatorAttachment,
  },
);
